import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCategories } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Loader2, 
    Ticket, 
    AlertCircle, 
    User,
    Building2,
    Paperclip,
    XCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuestTicketPage() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        ragione_sociale: '',
        oggetto: '',
        descrizione: '',
        categoria: '',
        priorita: 'Media'
    });
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const validFiles = selectedFiles.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                setError(`File "${file.name}" troppo grande (max 5MB)`);
                return false;
            }
            return true;
        });
        setFiles(prev => [...prev, ...validFiles]);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const submitData = new FormData();
            // Add form fields
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    submitData.append(key, formData[key]);
                }
            });
            // Add files if any
            if (files && files.length > 0) {
                files.forEach(file => {
                    submitData.append('files', file);
                });
            }
            
            const response = await axios.post(`${API_URL}/api/tickets/guest`, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data && response.data.numero_ticket) {
                // Redirect alla pagina di successo con i dati del ticket
                navigate('/ticket-inviato', { 
                    state: {
                        numero_ticket: response.data.numero_ticket,
                        ticket_id: response.data.ticket_id,
                        new_account_created: response.data.new_account_created,
                        email: formData.email
                    },
                    replace: true
                });
            } else {
                setError('Risposta non valida dal server');
            }
        } catch (err) {
            console.error('Error creating ticket:', err);
            setError(err.response?.data?.detail || 'Errore durante la creazione del ticket. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Assistenza Sistema Cert Italy</h1>
                                <p className="text-xs text-slate-500">Richiedi supporto</p>
                            </div>
                        </div>
                        <Link to="/login">
                            <Button variant="outline" size="sm">
                                Accedi
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">Come possiamo aiutarti?</h2>
                    <p className="text-slate-600">
                        Compila il form sottostante e ti risponderemo il prima possibile
                    </p>
                </div>

                <Card className="border-slate-200 animate-fade-in">
                    <CardHeader>
                        <CardTitle>Apri un Ticket di Assistenza</CardTitle>
                        <CardDescription>
                            Inserisci i tuoi dati e descrivi il problema. Riceverai le credenziali di accesso via email.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <Alert variant="destructive" className="bg-red-50 border-red-200">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            {/* Personal Info Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                    <User className="w-4 h-4" />
                                    I tuoi dati
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="nome">Nome *</Label>
                                        <Input
                                            id="nome"
                                            name="nome"
                                            placeholder="Mario"
                                            value={formData.nome}
                                            onChange={handleChange}
                                            required
                                            data-testid="guest-nome-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="cognome">Cognome *</Label>
                                        <Input
                                            id="cognome"
                                            name="cognome"
                                            placeholder="Rossi"
                                            value={formData.cognome}
                                            onChange={handleChange}
                                            required
                                            data-testid="guest-cognome-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email *</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="mario.rossi@email.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            data-testid="guest-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono">Telefono</Label>
                                        <Input
                                            id="telefono"
                                            name="telefono"
                                            type="tel"
                                            placeholder="+39 333 1234567"
                                            value={formData.telefono}
                                            onChange={handleChange}
                                            data-testid="guest-telefono-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="ragione_sociale" className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4" />
                                        Società / Ragione Sociale *
                                    </Label>
                                    <Input
                                        id="ragione_sociale"
                                        name="ragione_sociale"
                                        placeholder="Nome dell'azienda"
                                        value={formData.ragione_sociale}
                                        onChange={handleChange}
                                        required
                                        data-testid="guest-societa-input"
                                    />
                                </div>
                            </div>

                            {/* Ticket Info Section */}
                            <div className="space-y-4 pt-4 border-t border-slate-200">
                                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                    <Ticket className="w-4 h-4" />
                                    Dettagli richiesta
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="oggetto">Oggetto *</Label>
                                    <Input
                                        id="oggetto"
                                        name="oggetto"
                                        placeholder="Breve descrizione del problema"
                                        value={formData.oggetto}
                                        onChange={handleChange}
                                        required
                                        data-testid="guest-oggetto-input"
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="categoria">Categoria *</Label>
                                        <Select 
                                            value={formData.categoria} 
                                            onValueChange={(value) => setFormData({...formData, categoria: value})}
                                            required
                                        >
                                            <SelectTrigger data-testid="guest-categoria-select">
                                                <SelectValue placeholder="Seleziona categoria" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((cat) => (
                                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="priorita">Priorità</Label>
                                        <Select 
                                            value={formData.priorita} 
                                            onValueChange={(value) => setFormData({...formData, priorita: value})}
                                        >
                                            <SelectTrigger data-testid="guest-priorita-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Bassa">Bassa</SelectItem>
                                                <SelectItem value="Media">Media</SelectItem>
                                                <SelectItem value="Alta">Alta</SelectItem>
                                                <SelectItem value="Urgente">Urgente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="descrizione">Descrizione *</Label>
                                    <Textarea
                                        id="descrizione"
                                        name="descrizione"
                                        placeholder="Descrivi il problema in modo dettagliato. Includi eventuali messaggi di errore, passaggi per riprodurre il problema, etc."
                                        value={formData.descrizione}
                                        onChange={handleChange}
                                        rows={6}
                                        required
                                        data-testid="guest-descrizione-input"
                                    />
                                </div>

                                {/* File Attachments */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Paperclip className="w-4 h-4" />
                                        Allegati (opzionale)
                                    </Label>
                                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                                        <input
                                            type="file"
                                            id="file-upload"
                                            className="hidden"
                                            multiple
                                            onChange={handleFileSelect}
                                            data-testid="guest-file-input"
                                        />
                                        <label 
                                            htmlFor="file-upload" 
                                            className="flex flex-col items-center justify-center cursor-pointer py-2"
                                        >
                                            <Paperclip className="w-8 h-8 text-slate-400 mb-2" />
                                            <span className="text-sm text-slate-600">Clicca per aggiungere file</span>
                                            <span className="text-xs text-slate-400 mt-1">Max 5MB per file</span>
                                        </label>
                                    </div>
                                    
                                    {files.length > 0 && (
                                        <div className="space-y-2 mt-3">
                                            {files.map((file, index) => (
                                                <div 
                                                    key={index}
                                                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Paperclip className="w-4 h-4 text-indigo-500" />
                                                        <span className="text-sm text-slate-700">{file.name}</span>
                                                        <span className="text-xs text-slate-400">
                                                            ({(file.size / 1024).toFixed(1)} KB)
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="pt-4">
                                <Button
                                    type="submit"
                                    className="w-full bg-indigo-500 hover:bg-indigo-600 h-12 text-base"
                                    disabled={loading || !formData.nome || !formData.cognome || !formData.email || !formData.ragione_sociale || !formData.oggetto || !formData.descrizione || !formData.categoria}
                                    data-testid="guest-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Invio in corso...
                                        </>
                                    ) : (
                                        'Invia Richiesta'
                                    )}
                                </Button>
                                <p className="text-xs text-slate-500 text-center mt-3">
                                    Riceverai una email con le credenziali per accedere e seguire il tuo ticket
                                </p>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
